import dayjs from 'dayjs';

import { convertDateToUTC } from './date';

/**
 * Format FHIR model into friendly representation
 * @param model Array of FHIR models
 * @param field FHIR field
 * @param separator Join separator
 * @returns String rep of model
 */
export function formatFhirArray<T>(
  model: T[] | null | undefined,
  field?: keyof T,
  sep = ','
): string {
  if (!model) {
    return '-';
  }
  return model
    .map((el) => {
      if (field) {
        return el[field];
      }
      return el;
    })
    .join(sep);
}

export function isYear(str: string) {
  return new RegExp(/^\d{4}$/u, 'u').test(str);
}
export function isYearMonth(str: string) {
  return new RegExp(/^\d{4}-\d{2}$/u, 'u').test(str);
}
export function isYearMonthDay(str: string) {
  return new RegExp(/^\d{4}-\d{2}-\d{2}$/u, 'u').test(str);
}
export function isISO1(str: string) {
  // eslint-disable-next-line prefer-named-capture-group
  return new RegExp(
    // eslint-disable-next-line prefer-named-capture-group
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3,6}){0,1}(-|\+)\d{2}:\d{2}$/u,
    'u'
  ).test(str);
}
export function isISO2(str: string) {
  // eslint-disable-next-line prefer-named-capture-group
  return new RegExp(
    // eslint-disable-next-line prefer-named-capture-group
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3}){0,1}Z{0,1}$/u,
    'u'
  ).test(str);
}

export type FhirDatePrecision = 'year' | 'month' | 'day' | 'time';

/**
 * Number of leading characters carrying each precision:
 * YYYY / YYYY-MM / YYYY-MM-DD / YYYY-MM-DDTHH:mm:ss
 */
const FHIR_PRECISION_LENGTH: Record<FhirDatePrecision, number> = {
  year: 4,
  month: 7,
  day: 10,
  time: 19,
};

/**
 * Determine how precise a FHIR date string is
 * @param dateTime FHIR formatted date
 * @returns The precision, or null if the value is empty or not a FHIR date
 */
export function getFhirDatePrecision(
  dateTime: string | null | undefined
): FhirDatePrecision | null {
  if (!dateTime) {
    return null;
  }
  if (isYear(dateTime)) {
    return 'year';
  }
  if (isYearMonth(dateTime)) {
    return 'month';
  }
  if (isYearMonthDay(dateTime)) {
    return 'day';
  }
  if (isISO1(dateTime) || isISO2(dateTime)) {
    return 'time';
  }
  return null;
}

/**
 * Compare two FHIR dates at their coarsest common precision, so a year-only
 * value ("2024", typical of OCR extraction) never loses to a full timestamp
 * within the same year.
 *
 * Compares the raw string prefixes rather than parsing to a Date: FHIR date
 * forms are zero-padded and prefix-compatible, so lexical order is
 * chronological order. Parsing would drag in the local timezone — a timestamp
 * renders a day earlier west of UTC and a day later east of it — which would
 * flag valid same-day encounters. Note the tests run with TZ pinned to UTC, so
 * that class of bug would not surface there.
 *
 * @param a First FHIR date
 * @param b Second FHIR date
 * @returns -1, 0 or 1, or null when either value is empty or not a FHIR date
 */
export function compareFhirDates(
  a: string | null | undefined,
  b: string | null | undefined
): number | null {
  const precisionA = getFhirDatePrecision(a);
  const precisionB = getFhirDatePrecision(b);
  if (!a || !b || !precisionA || !precisionB) {
    return null;
  }

  const length = Math.min(
    FHIR_PRECISION_LENGTH[precisionA],
    FHIR_PRECISION_LENGTH[precisionB]
  );
  const truncatedA = a.slice(0, length);
  const truncatedB = b.slice(0, length);

  if (truncatedA === truncatedB) {
    return 0;
  }
  return truncatedA < truncatedB ? -1 : 1;
}

/**
 * Check whether a FHIR date falls strictly before another
 * @param value FHIR date to check
 * @param reference FHIR date to compare against
 * @returns `true` only when both values are comparable and `value` is earlier,
 * so incomplete or unrecognised input never reports as out of order
 */
export function isFhirDateBefore(
  value: string | null | undefined,
  reference: string | null | undefined
): boolean {
  const comparison = compareFhirDates(value, reference);
  return comparison !== null && comparison < 0;
}

/**
 * Format a FHIR date
 * @param dateTime FHIR Formatted Date
 * @param options Display options
 * @returns Formatted date string, or null if invalid date
 */
export function formatFhirDate(
  dateTime: string | null | undefined,
  options?: { showTime?: boolean; friendly?: boolean; adjustTz?: boolean }
): string | null {
  if (dateTime === null || dateTime === undefined) {
    return null;
  }
  const DATE_FORMATS = {
    year: {
      friendly: 'YYYY',
      formal: 'YYYY',
    },
    yearMonth: {
      friendly: 'MMM, YYYY',
      formal: 'MM/YYYY',
    },
    yearMonthDay: {
      friendly: 'MMM D, YYYY',
      formal: 'MM/DD/YYYY',
    },
    iso1: {
      friendly: 'MMM D, YYYY h:mm A',
      formal: 'MM/DD/YYYY hh:mm:ss A',
    },
    iso2: {
      friendly: 'MMM D, YYYY h:mm A',
      formal: 'MM/DD/YYYY hh:mm:ss A',
    },
  };
  const mode =
    options?.friendly || options?.friendly === undefined
      ? 'friendly'
      : 'formal';
  const showTime = options?.showTime === undefined || options.showTime;
  const adjustTz = Boolean(options?.adjustTz);

  let format: string | null = null;

  if (isYear(dateTime)) {
    format = DATE_FORMATS.year[mode];
  }
  if (isYearMonth(dateTime)) {
    format = DATE_FORMATS.yearMonth[mode];
  }
  if (isYearMonthDay(dateTime)) {
    format = DATE_FORMATS.yearMonthDay[mode];
  }
  if (isISO1(dateTime)) {
    if (showTime) {
      format = DATE_FORMATS.iso1[mode];
    } else {
      format = DATE_FORMATS.yearMonthDay[mode];
    }
  }
  if (isISO2(dateTime)) {
    if (showTime) {
      format = DATE_FORMATS.iso2[mode];
    } else {
      format = DATE_FORMATS.yearMonthDay[mode];
    }
  }
  if (format) {
    if (!adjustTz) {
      return dayjs(convertDateToUTC(dayjs(dateTime).toDate())).format(format);
    }
    return dayjs(dateTime).format(format);
  }
  return null;
}

/**
 * Get date components from a FHIR date
 * @param dateTime FHIR Formatted Date
 * @returns Object containing the components of the date
 */
export function getFhirDateComponents(dateTime: string | null | undefined):
  | {
      year?: string;
      month?: string;
      day?: string;
      time?: string;
    }
  | undefined {
  if (dateTime === null || dateTime === undefined) {
    return undefined;
  }
  if (isYear(dateTime)) {
    return { year: dayjs(dateTime).format('YYYY') };
  }
  if (isYearMonth(dateTime)) {
    return {
      year: dayjs(dateTime).format('YYYY'),
      month: dayjs(dateTime).format('MM'),
    };
  }
  if (isYearMonthDay(dateTime)) {
    return {
      year: dayjs(dateTime).format('YYYY'),
      month: dayjs(dateTime).format('MM'),
      day: dayjs(dateTime).format('DD'),
    };
  }
  if (isISO1(dateTime)) {
    return {
      year: dayjs(dateTime).format('YYYY'),
      month: dayjs(dateTime).format('MM'),
      day: dayjs(dateTime).format('DD'),
      time: dayjs(dateTime).format('hh:mm:ss a'),
    };
  }
  if (isISO2(dateTime)) {
    return {
      year: dayjs(dateTime).format('YYYY'),
      month: dayjs(dateTime).format('MM'),
      day: dayjs(dateTime).format('DD'),
      time: dayjs(dateTime).format('hh:mm:ss a'),
    };
  }
  return undefined;
}

/**
 * Get a date object from a FHIR date
 * @description Use for things like comparisons, sorting, etc.
 * @param dateTime FHIR Formatted Date
 * @param options Display options
 * @returns Formatted date string, or null if invalid date
 */
export function getFhirDate(
  dateTime: string | null | undefined,
  options?: { adjustTz?: boolean }
): Date | null {
  if (dateTime === null || dateTime === undefined) {
    return null;
  }
  const adjustTz = Boolean(options?.adjustTz);
  if (
    isYear(dateTime) ||
    isYearMonth(dateTime) ||
    isYearMonthDay(dateTime) ||
    isISO1(dateTime) ||
    isISO2(dateTime)
  ) {
    if (!adjustTz) {
      return dayjs(convertDateToUTC(dayjs(dateTime).toDate())).toDate();
    }
    return dayjs(dateTime).toDate();
  }
  return null;
}
